import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config.js';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { format } from 'date-fns';

const PTOSummaryCard = ({ currentUser }) => {
  const [ptoData, setPtoData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [ptoEnabled, setPtoEnabled] = useState(false);

  // Check if PTO is enabled for this tenant
useEffect(() => {
  const checkPTOEnabled = async () => {
    try {
      // Query for the pto_settings document
      const ptoDocRef = doc(db, 'tenantConfigurations', `${currentUser.tenantId}_pto_settings`);
      const ptoDoc = await getDoc(ptoDocRef);
      
      if (ptoDoc.exists()) {
        const configValue = ptoDoc.data().configValue;
        console.log('PTO config found:', configValue);
        setPtoEnabled(configValue?.enabled || false);
      } else {
        console.log('No PTO config found');
        setPtoEnabled(false);
      }
    } catch (err) {
      console.error('Error checking PTO enabled:', err);
      setPtoEnabled(false);
    }
  };
  
  if (currentUser?.tenantId) {
    checkPTOEnabled();
  }
}, [currentUser?.tenantId]);

  useEffect(() => {
    if (currentUser?.uid && currentUser?.tenantId && ptoEnabled) {
      fetchPTOBalance();
    } else if (!ptoEnabled) {
      setIsLoading(false);
    }
  }, [currentUser, ptoEnabled]);

  const fetchPTOBalance = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get current PTO year (July 1 - June 30)
      const today = new Date();
      const currentYear = today.getFullYear();
      const ptoYear = today.getMonth() >= 6 
        ? `${currentYear}-${currentYear + 1}` 
        : `${currentYear - 1}-${currentYear}`;

      console.log('Looking up faculty_staff by uid:', currentUser.uid);

      // First, find this user's faculty_staff document ID
      const facultyQuery = query(
        collection(db, 'faculty_staff'),
        where('uid', '==', currentUser.uid),
        where('tenantId', '==', currentUser.tenantId),
        limit(1)
      );

      const facultySnapshot = await getDocs(facultyQuery);

      if (facultySnapshot.empty) {
        console.log('No faculty_staff document found for uid:', currentUser.uid);
        setPtoData({ hasBalance: false });
        setIsLoading(false);
        return;
      }

      // Get the actual UID from the faculty document
const facultyData = facultySnapshot.docs[0].data();
const employeeUid = facultyData.uid || currentUser.uid;
console.log('Using employee UID:', employeeUid);

// Now query PTO balance using the employee UID
const balanceQuery = query(
  collection(db, 'employee_pto_balances'),
  where('employeeId', '==', employeeUid),
  where('tenantId', '==', currentUser.tenantId),
  where('ptoYear', '==', ptoYear),
  limit(1)
);

const balanceSnapshot = await getDocs(balanceQuery);

if (balanceSnapshot.empty) {
  console.log('No PTO balance found for employeeId:', employeeUid);
  setPtoData({ hasBalance: false });
  setIsLoading(false);
  return;
}

      const balanceDoc = balanceSnapshot.docs[0];
      const balance = balanceDoc.data();

      console.log('Found PTO balance:', balance);

      // Calculate remaining days
      const daysRemaining = balance.daysAllocated - balance.daysUsed - balance.daysPending;

      // Calculate days until next reset (July 1)
      const nextResetYear = today.getMonth() >= 6 ? currentYear + 1 : currentYear;
      const nextReset = new Date(nextResetYear, 6, 1);
      const daysUntilReset = Math.ceil((nextReset - today) / (1000 * 60 * 60 * 24));

      setPtoData({
        hasBalance: true,
        balance: {
          id: balanceDoc.id,
          ptoYear: balance.ptoYear,
          daysAllocated: balance.daysAllocated,
          daysUsed: balance.daysUsed || 0,
          daysPending: balance.daysPending || 0,
          daysRemaining: parseFloat(daysRemaining.toFixed(2)),
          lastUpdated: balance.lastUpdated,
          notes: balance.notes
        },
        employee: {
          firstName: currentUser.firstName || currentUser.displayName?.split(' ')[0] || '',
          lastName: currentUser.lastName || currentUser.displayName?.split(' ')[1] || ''
        },
        resetInfo: {
          nextResetDate: nextReset.toISOString().split('T')[0],
          daysUntilReset: daysUntilReset
        }
      });

    } catch (err) {
      console.error('Error fetching PTO balance:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show anything if PTO is disabled
  if (!ptoEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        textAlign: 'center',
        color: '#6c757d'
      }}>
        Loading PTO information...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        color: '#856404'
      }}>
        <strong>⚠️ Could not load PTO data:</strong> {error}
      </div>
    );
  }

  if (!ptoData?.hasBalance) {
    return (
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        textAlign: 'center',
        color: '#6c757d'
      }}>
        <p style={{ margin: 0 }}>
          No PTO balance found. Contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  const { balance, resetInfo } = ptoData;
  const usagePercentage = balance.daysAllocated > 0 
    ? (balance.daysUsed / balance.daysAllocated) * 100 
    : 0;

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', color: '#333' }}>
            🏖️ Your PTO Balance
          </h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
            PTO Year: {balance.ptoYear}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '4px' }}>
            Resets on
          </div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
            {format(new Date(resetInfo.nextResetDate), 'MMM d, yyyy')}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            ({resetInfo.daysUntilReset} days)
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '15px',
        marginBottom: '20px'
      }}>
        {/* Days Remaining */}
        <div style={{
          padding: '15px',
          backgroundColor: '#e7f5ff',
          borderRadius: '8px',
          border: '1px solid #339af0'
        }}>
          <div style={{ fontSize: '12px', color: '#1971c2', marginBottom: '5px', fontWeight: '600' }}>
            AVAILABLE
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1971c2' }}>
            {balance.daysRemaining}
          </div>
          <div style={{ fontSize: '12px', color: '#495057' }}>days</div>
        </div>

        {/* Days Used */}
        <div style={{
          padding: '15px',
          backgroundColor: '#fff0f6',
          borderRadius: '8px',
          border: '1px solid #d6336c'
        }}>
          <div style={{ fontSize: '12px', color: '#c2255c', marginBottom: '5px', fontWeight: '600' }}>
            USED
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#c2255c' }}>
            {balance.daysUsed}
          </div>
          <div style={{ fontSize: '12px', color: '#495057' }}>days</div>
        </div>

        {/* Days Pending - Only show if > 0 */}
        {balance.daysPending > 0 && (
          <div style={{
            padding: '15px',
            backgroundColor: '#fff9db',
            borderRadius: '8px',
            border: '1px solid #fab005'
          }}>
            <div style={{ fontSize: '12px', color: '#f08c00', marginBottom: '5px', fontWeight: '600' }}>
              PENDING
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f08c00' }}>
              {balance.daysPending}
            </div>
            <div style={{ fontSize: '12px', color: '#495057' }}>days</div>
          </div>
        )}

        {/* Total Allocated */}
        <div style={{
          padding: '15px',
          backgroundColor: '#e7f5e7',
          borderRadius: '8px',
          border: '1px solid #51cf66'
        }}>
          <div style={{ fontSize: '12px', color: '#2f9e44', marginBottom: '5px', fontWeight: '600' }}>
            TOTAL
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2f9e44' }}>
            {balance.daysAllocated}
          </div>
          <div style={{ fontSize: '12px', color: '#495057' }}>days/year</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ marginTop: '20px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px',
          fontSize: '13px',
          color: '#495057'
        }}>
          <span>PTO Usage</span>
          <span>{usagePercentage.toFixed(0)}% used</span>
        </div>
        <div style={{
          width: '100%',
          height: '10px',
          backgroundColor: '#e9ecef',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.min(usagePercentage, 100)}%`,
            height: '100%',
            backgroundColor: usagePercentage > 90 ? '#dc3545' : usagePercentage > 70 ? '#ffc107' : '#28a745',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Warning Messages */}
      {balance.daysRemaining < 1 && (
        <div style={{
          marginTop: '15px',
          padding: '12px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#856404'
        }}>
          ⚠️ You have no PTO days remaining. Any additional absences will be unpaid.
        </div>
      )}

      {balance.daysRemaining < 3 && balance.daysRemaining >= 1 && (
        <div style={{
          marginTop: '15px',
          padding: '12px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#856404'
        }}>
          ⚠️ Low PTO balance: Only {balance.daysRemaining} days remaining
        </div>
      )}
    </div>
  );
};

export default PTOSummaryCard;